/**
 * Payroll adjustment sheet — classified correction entry surface.
 *
 * Invoked from a workbook row. The operator picks a field to correct,
 * chooses a classification, and — for non-run_only — confirms with gg42.
 * Submission hits POST /api/finance/payroll/run/:id/adjustment. Server
 * enforces the same classification + confirmation rules as a second fence.
 */

import React, { useState } from 'react';
import { C } from '../../shared/design-tokens.js';
import type { PayrollWorkbookRow } from './use-payroll-workbook.js';

type Classification = 'run_only' | 'forward_going' | 'effective_dated';

interface PayrollAdjustmentSheetProps {
  row: PayrollWorkbookRow;
  runId: string;
  onClose: () => void;
  onSubmit: (body: {
    detail_row_id: string;
    employee_id: string | null;
    field_name: string;
    original_value: string | number | null;
    corrected_value: string | number;
    classification: Classification;
    effective_date?: string;
    reason?: string;
    confirmation_code?: string;
  }) => Promise<{ ok: boolean; error?: string }>;
}

const FIELD_OPTIONS: Array<{ key: string; label: string; canonical: boolean; kind: 'number' | 'text' }> = [
  { key: 'pay_rate',          label: 'Pay rate',               canonical: true,  kind: 'number' },
  { key: 'rate_type',         label: 'Rate type (daily/hourly/salary)', canonical: true, kind: 'text' },
  { key: 'per_diem_rate',     label: 'Per diem rate',          canonical: true,  kind: 'number' },
  { key: 'days',              label: 'Days worked',            canonical: false, kind: 'number' },
  { key: 'hours',             label: 'Hours worked',           canonical: false, kind: 'number' },
  { key: 'base_pay',          label: 'Base pay',               canonical: false, kind: 'number' },
  { key: 'stop_bonus_amount', label: 'Stop bonus amount',      canonical: false, kind: 'number' },
  { key: 'bonus',             label: 'Bonus',                  canonical: false, kind: 'number' },
  { key: 'holiday_pay',       label: 'Holiday pay',            canonical: false, kind: 'number' },
  { key: 'peak_bonus',        label: 'Peak bonus',             canonical: false, kind: 'number' },
  { key: 'reimbursement',     label: 'Reimbursement',          canonical: false, kind: 'number' },
];

function currentValueForField(row: PayrollWorkbookRow, field: string): string {
  switch (field) {
    case 'pay_rate': return row.rate != null ? String(row.rate) : '';
    case 'rate_type': return row.rate_type || '';
    case 'per_diem_rate': return row.per_diem_rate != null ? String(row.per_diem_rate) : '';
    case 'days': return row.days != null ? String(row.days) : '';
    case 'base_pay': return row.base_pay != null ? String(row.base_pay) : '';
    case 'stop_bonus_amount': return row.stop_bonus_amount != null ? String(row.stop_bonus_amount) : '';
    case 'per_diem_pay': return row.per_diem_pay != null ? String(row.per_diem_pay) : '';
    default: return '';
  }
}

export default function PayrollAdjustmentSheet({ row, runId, onClose, onSubmit }: PayrollAdjustmentSheetProps) {
  const [field, setField] = useState<string>('pay_rate');
  const [classification, setClassification] = useState<Classification>('run_only');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [correctedValue, setCorrectedValue] = useState('');
  const [reason, setReason] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedField = FIELD_OPTIONS.find((f) => f.key === field) || FIELD_OPTIONS[0];
  const needsGg42 = classification !== 'run_only';
  const runOnlyDisabledForCanonical = selectedField.canonical && classification === 'run_only';
  const canonicalNote = selectedField.canonical
    ? 'Canonical comp field — cannot be corrected run-only. Pick Going forward or Effective date.'
    : null;

  const originalValue = currentValueForField(row, field);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!row.detail_row_id) { setErr('Row has no detail_row_id — cannot adjust.'); return; }
    if (!correctedValue) { setErr('Enter a corrected value.'); return; }
    if (classification === 'effective_dated' && !effectiveDate) { setErr('Effective date is required.'); return; }
    if (needsGg42 && confirmationCode.trim().toLowerCase() !== 'gg42') {
      setErr('Confirmation code required: enter gg42 to apply a forward-going or effective-dated change.');
      return;
    }
    if (runOnlyDisabledForCanonical) {
      setErr('Canonical fields cannot be run-only. Pick Going forward or Effective date.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await onSubmit({
        detail_row_id: row.detail_row_id,
        employee_id: row.employee_id,
        field_name: field,
        original_value: originalValue || null,
        corrected_value: selectedField.kind === 'number' ? parseFloat(correctedValue) : correctedValue,
        classification,
        effective_date: classification === 'effective_dated' ? effectiveDate : undefined,
        reason: reason || undefined,
        confirmation_code: needsGg42 ? confirmationCode : undefined,
      });
      if (!resp.ok) { setErr(resp.error || 'Submit failed'); return; }
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 24,
    }}>
      <form onSubmit={handleSubmit} style={{
        background: C.surface, borderRadius: 12, border: `1px solid ${C.borderSoft}`,
        width: 560, maxWidth: '100%', padding: 20, display: 'grid', gap: 14, color: C.text,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Adjust payroll row</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>
              {row.first_name} {row.last_name}{row.driver_id ? ` · ${row.driver_id}` : ''}{row.terminal ? ` · ${row.terminal}` : ''}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        <div>
          <label style={labelStyle}>Field</label>
          <select value={field} onChange={(e) => { setField(e.target.value); setCorrectedValue(''); }} style={inputStyle}>
            {FIELD_OPTIONS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}{f.canonical ? ' (canonical)' : ''}</option>
            ))}
          </select>
          {canonicalNote && <div style={{ fontSize: 11, color: C.gold, marginTop: 4 }}>{canonicalNote}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Current value</label>
            <input value={originalValue} disabled style={{ ...inputStyle, opacity: 0.6 }} />
          </div>
          <div>
            <label style={labelStyle}>Corrected value</label>
            <input
              value={correctedValue}
              onChange={(e) => setCorrectedValue(e.target.value)}
              type={selectedField.kind === 'number' ? 'number' : 'text'}
              step={selectedField.kind === 'number' ? 'any' : undefined}
              required
              style={inputStyle}
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Classification</label>
          <div style={{ display: 'grid', gap: 6 }}>
            {(['run_only', 'forward_going', 'effective_dated'] as Classification[]).map((c) => (
              <label key={c} style={{ display: 'flex', gap: 8, fontSize: 13, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="classification"
                  value={c}
                  checked={classification === c}
                  onChange={() => setClassification(c)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>
                    {c === 'run_only' ? 'Run-only' : c === 'forward_going' ? 'Going forward' : 'Effective from a specific date'}
                  </span>
                  <span style={{ fontSize: 11, color: C.textMuted, display: 'block', marginTop: 2 }}>
                    {c === 'run_only' && 'Affects only this payroll run. Canonical comp truth (rate / rate_type / per-diem rate) unchanged.'}
                    {c === 'forward_going' && 'Supersedes the employee’s active compensation profile starting today. Applies to this run too.'}
                    {c === 'effective_dated' && 'Creates a compensation profile effective from a named date (past, present, or future).'}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {classification === 'effective_dated' && (
          <div>
            <label style={labelStyle}>Effective date</label>
            <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} required style={inputStyle} />
          </div>
        )}

        <div>
          <label style={labelStyle}>Reason (optional)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} style={{ ...inputStyle, fontFamily: 'inherit' }} />
        </div>

        {needsGg42 && (
          <div>
            <label style={labelStyle}>Confirmation code <span style={{ color: C.gold }}>(required for non-run-only)</span></label>
            <input
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              placeholder="gg42"
              style={inputStyle}
              autoComplete="off"
            />
          </div>
        )}

        {err && <div style={{ fontSize: 12, color: C.red }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn btn-ghost" disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting || runOnlyDisabledForCanonical}>
            {submitting ? 'Saving…' : 'Apply adjustment'}
          </button>
        </div>
      </form>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, color: C.textMuted,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: 13,
  background: C.panel, color: C.text,
  border: `1px solid ${C.borderSoft}`, borderRadius: 6,
};
